import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import api from '../utils/api';

const loadRazorpay = () => new Promise((resolve) => {
  if (window.Razorpay) return resolve(true);
  const script = document.createElement('script');
  script.src = 'https://checkout.razorpay.com/v1/checkout.js';
  script.onload = () => resolve(true);
  script.onerror = () => resolve(false);
  document.body.appendChild(script);
});

const getRealStatus = (auction) => {
  const now   = new Date();
  const start = new Date(auction.start_time);
  const end   = new Date(auction.end_time);
  if (auction.status === 'cancelled') return 'cancelled';
  if (now < start)               return 'upcoming';
  if (now >= start && now <= end) return 'live';
  return 'ended';
};

const CountdownTimer = ({ endTime, startTime, status }) => {
  const target = status === 'upcoming' ? startTime : endTime;
  const calc = useCallback(() => {
    const diff = new Date(target) - new Date();
    if (diff <= 0) return { d: 0, h: 0, m: 0, s: 0, over: true };
    return {
      d: Math.floor(diff / 86400000),
      h: Math.floor((diff % 86400000) / 3600000),
      m: Math.floor((diff % 3600000) / 60000),
      s: Math.floor((diff % 60000) / 1000),
      over: false
    };
  }, [target]);

  const [time, setTime] = useState(calc);
  useEffect(() => {
    const t = setInterval(() => setTime(calc()), 1000);
    return () => clearInterval(t);
  }, [calc]);

  if (time.over) return <span className="text-red-500 font-bold">Ended</span>;

  const Box = ({ val, label }) => (
    <div className="text-center">
      <div className="bg-slate-900 text-white rounded-2xl w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center text-lg sm:text-2xl font-headline font-black">
        {String(val).padStart(2, '0')}
      </div>
      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">{label}</p>
    </div>
  );

  return (
    <div className="flex items-end gap-2 sm:gap-3 flex-wrap">
      {time.d > 0 && <Box val={time.d} label="Days" />}
      <Box val={time.h} label="Hours" />
      <Box val={time.m} label="Mins" />
      <Box val={time.s} label="Secs" />
    </div>
  );
};

const AuctionDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [auction, setAuction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bidAmount, setBidAmount] = useState('');
  const [bidding, setBidding] = useState(false);
  const [bidError, setBidError] = useState('');
  const [bidSuccess, setBidSuccess] = useState('');
  const [isRegistered, setIsRegistered] = useState(false);
  const [registering, setRegistering] = useState(false);

  const { user } = useAuth();

  const fetchAuction = useCallback(async () => {
  try {
    const res = await api.get(`/api/auctions/${id}`);
    setAuction(res.data);
    if (user?.id) {
      try {
        const regRes = await api.get(`/api/auctions/${id}/register-status`);
        setIsRegistered(regRes.data.registered);
      } catch {
        setIsRegistered(false);
      }
    }
    const minBid = (res.data.current_bid || res.data.start_price) + (res.data.min_increment || 500);
    setBidAmount(minBid);
  } catch (err) {
    setError(err.message);
  } finally {
    setLoading(false);
  }
}, [id, user?.id]);

  useEffect(() => {
    fetchAuction();
    // Auto-refresh every 15s for live auctions
    const interval = setInterval(() => fetchAuction(), 15000);
    return () => clearInterval(interval);
  }, [fetchAuction]);

  const handleBid = async () => {
    setBidError('');
    setBidSuccess('');
    if (!user?.token) { navigate('/login'); return; }
    if (!bidAmount || isNaN(bidAmount)) { setBidError('Please enter a valid bid amount'); return; }
    if (Number(bidAmount) < minBid) { setBidError(`Minimum bid is ₹${minBid.toLocaleString('en-IN')}`); return; }
    setBidding(true);
    try {
      await api.post(`/api/auctions/${id}/bid`, { bid_amount: Number(bidAmount) });
      setBidSuccess(`Bid of ₹${Number(bidAmount).toLocaleString('en-IN')} placed successfully!`);
      fetchAuction();
    } catch (err) {
      setBidError(err.response?.data?.error || err.message);
    } finally {
      setBidding(false);
    }
  };

  const handleRegister = async () => {
    if (!user?.id) { navigate('/login'); return; }
    setRegistering(true);
    setBidError('');
    const loaded = await loadRazorpay();
    if (!loaded) { setBidError('Payment gateway failed to load. Try again.'); setRegistering(false); return; }

    try {
      const { data } = await api.post('/api/payment/create-deposit', { auction_id: id });

      const options = {
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: 'Elite Goats',
        description: 'Auction Deposit (Refundable)',
        order_id: data.razorpay_order_id,
        handler: async (response) => {
          try {
            await api.post('/api/payment/verify-deposit', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              auction_id: id,
            });
            setIsRegistered(true);
          } catch (err) {
            setBidError('Payment verification failed. Contact support.');
          }
        },
        prefill: {
          name: user.name,
          email: user.email,
          contact: user.phone,
        },
        theme: { color: '#06A788' },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      setBidError(err.response?.data?.error || 'Registration failed');
    } finally {
      setRegistering(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error || !auction) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center space-y-4">
        <p className="text-xl font-bold">Auction not found</p>
        <button onClick={() => navigate('/auctions')}
          className="px-6 py-3 bg-primary text-white rounded-full font-bold">
          Back to Auctions
        </button>
      </div>
    </div>
  );

  const minBid = (Number(auction.current_bid) > 0 ? Number(auction.current_bid) : Number(auction.start_price)) + Number(auction.min_increment || 500);
  const realStatus = getRealStatus(auction);
  const isLive = realStatus === 'live';

  return (
    <div className="min-h-screen bg-background text-slate-900 font-body antialiased">
      <Navbar variant="default" />

      <main className="pt-28 pb-20 max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-12 lg:px-12">

        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-slate-400 mb-10">
          <span onClick={() => navigate('/')} className="hover:text-primary cursor-pointer">Home</span>
          <span className="material-symbols-outlined text-xs">chevron_right</span>
          <span onClick={() => navigate('/auctions')} className="hover:text-primary cursor-pointer">Auctions</span>
          <span className="material-symbols-outlined text-xs">chevron_right</span>
          <span className="text-slate-600 font-medium">{auction.title || auction.animal_name}</span>
        </nav>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-start">

          {/* Left — Image */}
          <div className="space-y-6">
            <div className="relative rounded-3xl overflow-hidden aspect-square"
                 style={{ boxShadow: '0 30px 60px -15px rgba(0,107,86,0.12)' }}>
              <img
                src={auction.image_url || 'https://images.unsplash.com/photo-1524593166156-312f362cada0?w=800'}
                alt={auction.animal_name}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-6 left-6">
                {realStatus === 'live' && (
                  <span className="bg-red-500 text-white px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest flex items-center gap-2">
                    <span className="w-2 h-2 bg-white rounded-full animate-pulse inline-block" />
                    Live Auction
                  </span>
                )}
                {realStatus === 'upcoming' && (
                  <span className="bg-amber-400 text-white px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest">
                    Upcoming
                  </span>
                )}
                {realStatus === 'ended' && (
                  <span className="bg-slate-500 text-white px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest">
                    Ended
                  </span>
                )}
              </div>
            </div>

            {/* Animal details */}
            <div className="bg-white rounded-3xl p-5 sm:p-8 border border-slate-100 space-y-6"
                 style={{ boxShadow: '0 10px 40px -10px rgba(0,107,86,0.06)' }}>
              <h3 className="text-xs font-black uppercase tracking-widest text-primary">Animal Details</h3>
              <div className="grid grid-cols-2 gap-4 sm:gap-6">
                {[
                  { icon: 'category',       label: 'Category', value: auction.category },
                  { icon: 'pets',           label: 'Breed',    value: auction.breed },
                  { icon: 'fitness_center', label: 'Weight',   value: `${auction.weight_kg} kg` },
                  { icon: 'calendar_month', label: 'Age',      value: `${auction.age_months} months` },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <span className="material-symbols-outlined text-primary text-sm">{item.icon}</span>
                    </div>
                    <div>
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{item.label}</p>
                      <p className="text-sm font-bold text-slate-900">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>
              {auction.animal_description && (
                <p className="text-sm text-slate-500 leading-relaxed border-t border-slate-100 pt-6">
                  {auction.animal_description}
                </p>
              )}
            </div>
          </div>

          {/* Right — Bid Panel */}
          <div className="space-y-8">
            <div>
              <span className="text-primary font-bold tracking-[0.4em] uppercase text-xs block mb-3">
                {auction.category} Auction
              </span>
              <h1 className="text-2xl sm:text-3xl lg:text-5xl font-headline font-bold text-slate-900 leading-tight">
                {auction.title || auction.animal_name}
              </h1>
              <p className="text-slate-500 mt-2 flex items-center gap-2">
                <span className="material-symbols-outlined text-primary text-sm">location_on</span>
                {auction.city}{auction.state ? `, ${auction.state}` : ''}
              </p>
            </div>

            {/* Timer */}
            {(isLive || realStatus === 'upcoming') && (
              <div className="bg-slate-50 rounded-3xl p-8 border border-slate-100">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">
                  {isLive ? 'Ends In' : 'Starts In'}
                </p>
                <CountdownTimer
                  endTime={auction.end_time}
                  startTime={auction.start_time}
                  status={realStatus}
                />
              </div>
            )}

            {/* Current bid */}
            <div className="bg-white rounded-3xl p-5 sm:p-8 border border-slate-100 space-y-6"
                 style={{ boxShadow: '0 10px 40px -10px rgba(0,107,86,0.06)' }}>
              <div className="grid grid-cols-2 gap-4 sm:gap-8">
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">
                    {Number(auction.current_bid) > 0 ? 'Current Bid' : 'Starting Price'}
                  </p>
                  <p className="text-2xl sm:text-4xl font-headline font-black text-primary">
                    ₹{Number(auction.current_bid) > 0
                        ? Number(auction.current_bid).toLocaleString('en-IN')
                        : Number(auction.start_price).toLocaleString('en-IN')}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Total Bids</p>
                  <p className="text-2xl sm:text-4xl font-headline font-black text-slate-900">{auction.total_bids}</p>
                </div>
              </div>
              <p className="text-xs text-slate-400">
                Min. increment: <span className="font-bold text-slate-600">₹{Number(auction.min_increment || 500).toLocaleString('en-IN')}</span>
                {' · '}Next min bid: <span className="font-bold text-primary">₹{minBid.toLocaleString('en-IN')}</span>
              </p>
            </div>

            {/* Bid input */}
            {isLive && (
              <div className="bg-white rounded-3xl p-8 border border-primary/20 space-y-5"
                   style={{ boxShadow: '0 10px 40px -10px rgba(0,107,86,0.1)' }}>
                <h3 className="text-sm font-black uppercase tracking-widest text-slate-900">Place Your Bid</h3>
                {!isRegistered ? (
                  <div className="text-center space-y-4 py-4">
                    <p className="text-slate-500 text-sm">A refundable deposit of <span className="font-black text-slate-900">₹25,000</span> is required to participate in bidding.</p>
                    {bidError && <p className="text-red-500 text-sm font-medium">{bidError}</p>}
                    <button
                      onClick={handleRegister}
                      disabled={registering}
                      className="w-full py-4 bg-amber-500 text-white rounded-full font-bold text-sm tracking-wide hover:bg-amber-600 transition-all disabled:opacity-60"
                    >
                      {registering ? 'Registering...' : '💰 Register to Bid (₹25,000 deposit)'}
                    </button>
                  </div>
                ) : (
                   <>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xl font-bold text-slate-400">₹</span>
                  <input
                    type="number"
                    value={bidAmount}
                    onChange={(e) => setBidAmount(e.target.value)}
                    min={minBid}
                    step={auction.min_increment || 500}
                    className="w-full bg-slate-50 border border-slate-200 rounded-2xl py-4 pl-10 pr-4 text-xl font-bold text-slate-900 focus:ring-2 focus:ring-primary outline-none"
                  />
                </div>
                {/* Quick bid buttons */}
                <div className="flex gap-2 flex-wrap">
                  {[0, 1, 2, 3].map(i => {
                    const val = minBid + (i * Number(auction.min_increment || 500));
                    return (
                      <button key={i} onClick={() => setBidAmount(val)}
                        className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-all
                          ${Number(bidAmount) === val
                            ? 'bg-primary text-white border-primary'
                            : 'border-slate-200 text-slate-600 hover:border-primary/50'
                          }`}>
                        ₹{val.toLocaleString('en-IN')}
                      </button>
                    );
                  })}
                </div>
                {bidError && <p className="text-red-500 text-sm font-medium">{bidError}</p>}
                {bidSuccess && <p className="text-emerald-500 text-sm font-bold">{bidSuccess}</p>}
                <button
                  onClick={handleBid}
                  disabled={bidding}
                  className="w-full py-4 bg-primary text-white rounded-full font-bold text-sm tracking-wide shadow-lg shadow-primary/20 hover:bg-emerald-700 transition-all disabled:opacity-60"
                >
                  {bidding ? 'Placing Bid...' : user?.id ? '🔨 Place Bid' : 'Login to Bid'}
                </button>
                </>
                )}
              </div>
            )}

            {/* Seller */}
            <div className="flex items-center justify-between p-4 sm:p-6 bg-slate-50 rounded-2xl border border-slate-100 cursor-pointer hover:border-primary/30 transition-all"
                 onClick={() => navigate(`/breeder/${auction.seller_public_id || auction.seller_id}`)}>
              <div className="flex items-center gap-4">
                <img
                  src={auction.seller_image || `https://ui-avatars.com/api/?name=${encodeURIComponent(auction.seller_name)}&size=60&background=06A788&color=fff`}
                  alt={auction.seller_name}
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div>
                  <p className="font-bold text-slate-900">{auction.seller_name}</p>
                  <p className="text-xs text-slate-400">{auction.city}{auction.state ? `, ${auction.state}` : ''}</p>
                </div>
              </div>
              <span className="material-symbols-outlined text-slate-400">chevron_right</span>
            </div>

            {/* Recent bids */}
            {auction.recent_bids?.length > 0 && (
              <div className="bg-white rounded-3xl p-8 border border-slate-100 space-y-4"
                   style={{ boxShadow: '0 10px 40px -10px rgba(0,107,86,0.06)' }}>
                <h3 className="text-xs font-black uppercase tracking-widest text-primary">Recent Bids</h3>
                <div className="space-y-3">
                  {auction.recent_bids.map((bid, i) => (
                    <div key={i} className="flex items-center justify-between py-3 border-b border-slate-50 last:border-0">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold
                          ${i === 0 ? 'bg-primary text-white' : 'bg-slate-100 text-slate-500'}`}>
                          {i === 0 ? '🏆' : i + 1}
                        </div>
                        <span className="text-sm font-medium text-slate-700">{bid.bidder_name}</span>
                      </div>
                      <div className="text-right">
                        <p className={`font-black ${i === 0 ? 'text-primary text-lg' : 'text-slate-700'}`}>
                          ₹{Number(bid.bid_amount).toLocaleString('en-IN')}
                        </p>
                        <p className="text-[10px] text-slate-400">
                          {new Date(bid.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer variant="default" />
    </div>
  );
};

export default AuctionDetail;
